import { apiClient } from './axios';
import type { CarePhase } from './flowApi';

export enum HepatoFormStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  REVIEWED = 'REVIEWED',
  ARCHIVED = 'ARCHIVED',
}

export type HepatoSection = Record<string, unknown>;

export interface HepatoForm {
  id: string;
  patientId: string;
  userId: string;
  createdBy: string;
  status: HepatoFormStatus;
  phase?: CarePhase;
  programType: string;
  generalData?: HepatoSection;
  currentSymptoms?: HepatoSection;
  personalHistory?: HepatoSection;
  familyHistory?: HepatoSection;
  riskFactors?: HepatoSection;
  previousExams?: HepatoSection;
  initialDiagnosis?: HepatoSection;
  diseaseStage?: HepatoSection;
  labsAndImaging?: HepatoSection;
  surgery?: HepatoSection;
  oncologicTreatment?: HepatoSection;
  treatmentResponse?: HepatoSection;
  followUp?: HepatoSection;
  clinicalTrials?: HepatoSection;
  consent?: HepatoSection;
  notes?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface CreateHepatoFormDto {
  patientId: string;
  programType?: string;
  generalData?: HepatoSection;
  currentSymptoms?: HepatoSection;
  personalHistory?: HepatoSection;
  familyHistory?: HepatoSection;
  riskFactors?: HepatoSection;
  previousExams?: HepatoSection;
  initialDiagnosis?: HepatoSection;
  diseaseStage?: HepatoSection;
  labsAndImaging?: HepatoSection;
  surgery?: HepatoSection;
  oncologicTreatment?: HepatoSection;
  treatmentResponse?: HepatoSection;
  followUp?: HepatoSection;
  clinicalTrials?: HepatoSection;
  consent?: HepatoSection;
  notes?: string;
}

export type UpdateHepatoFormDto = Partial<CreateHepatoFormDto>;

export type TemplatePlaceholderStatus = 'collected' | 'derived' | 'missing-ui' | 'signature' | 'ignored-phase';

export interface TemplatePlaceholder {
  key: string;
  section: string;
  status: TemplatePlaceholderStatus;
  source: string;
}

export interface TemplatePlaceholderResponse {
  programType: 'DERMATO' | 'HEPATO';
  template: string;
  scope: string;
  placeholders: TemplatePlaceholder[];
  totals: Record<TemplatePlaceholderStatus | 'all' | 'active' | 'ignored', number>;
}

const unwrapResponse = <T>(responseData: any): T => responseData?.data ?? responseData;

const hepatoApi = {
  async create(data: CreateHepatoFormDto): Promise<HepatoForm> {
    const response = await apiClient.post('/hepato-forms', data);
    return unwrapResponse<HepatoForm>(response.data);
  },

  async getById(id: string): Promise<HepatoForm> {
    const response = await apiClient.get(`/hepato-forms/${id}`);
    return unwrapResponse<HepatoForm>(response.data);
  },

  async update(id: string, data: UpdateHepatoFormDto): Promise<HepatoForm> {
    const response = await apiClient.patch(`/hepato-forms/${id}`, data);
    return unwrapResponse<HepatoForm>(response.data);
  },

  async submit(id: string): Promise<HepatoForm> {
    const response = await apiClient.post(`/hepato-forms/${id}/submit`);
    return unwrapResponse<HepatoForm>(response.data);
  },

  async list(params?: {
    page?: number;
    limit?: number;
    patientId?: string;
    status?: HepatoFormStatus;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    createdAfter?: string;
    createdBefore?: string;
  }): Promise<{ data: HepatoForm[]; total: number; page: number; limit: number }> {
    const response = await apiClient.get('/hepato-forms', { params });
    const payload = response.data.data ?? response.data;
    const forms = Array.isArray(payload) ? payload : payload.data ?? [];
    const meta = response.data.meta ?? payload.meta;

    return {
      data: forms,
      total: payload.total ?? meta?.total ?? forms.length,
      page: payload.page ?? meta?.page ?? params?.page ?? 1,
      limit: payload.limit ?? meta?.limit ?? params?.limit ?? 20,
    };
  },

  async getTemplatePlaceholders(): Promise<TemplatePlaceholderResponse> {
    const response = await apiClient.get('/hepato-forms/template-placeholders');
    return unwrapResponse<TemplatePlaceholderResponse>(response.data);
  },

  /** Export PDF al chestionarului/anamnezei (secțiunile I–VI) în ro | en | srb. */
  async downloadAnamnesisPdf(id: string, lang: 'ro' | 'en' | 'srb' = 'ro'): Promise<Blob> {
    const response = await apiClient.get(`/hepato-forms/${id}/export.pdf`, {
      params: { lang },
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  /** Export PDF complet (anamneză + toți pașii clinici) în ro | en | srb. */
  async downloadFullPdf(id: string, lang: 'ro' | 'en' | 'srb' = 'ro'): Promise<Blob> {
    const response = await apiClient.get(`/hepato-forms/${id}/export-full.pdf`, {
      params: { lang },
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/hepato-forms/${id}`);
  },
};

export default hepatoApi;
